import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './clientRankingStat.css';

function formatScore(n) {
    const num = Number(n || 0);
    return num.toFixed(2);
}

function formatPropertyId(id) {
    const n = Number(id);
    if (!Number.isFinite(n)) return String(id ?? '');
    return `#${String(n).padStart(5, '0')}`;
}

function safeText(v) {
    return v === null || v === undefined || v === '' ? '-' : String(v);
}

function buildUnitLine(u) {
    const building = safeText(u.address_line_1);
    const unit = safeText(u.unit_number);
    const area = safeText(u.area);
    const city = safeText(u.city);
    return `${building} - ${unit} • ${area} • ${city}`;
}

function StatisticsModal({ isOpen, onClose, client, days }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [units, setUnits] = useState([]);

    useEffect(() => {
        if (!isOpen || !client?.client_id) return;

        const run = async () => {
            setLoading(true);
            setError('');
            setUnits([]);
            try {
                const res = await axios.get(`/api/statistics/clients/${client.client_id}/unit-rankings`, {
                    params: { days },
                    withCredentials: true,
                });
                setUnits(Array.isArray(res.data?.units) ? res.data.units : []);
            } catch (e) {
                console.error('Failed to load unit rankings', e);
                setError('Failed to load unit rankings.');
            } finally {
                setLoading(false);
            }
        };

        run();
    }, [isOpen, client?.client_id, days]);

    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="statistics-modal-overlay" onMouseDown={onClose}>
            <div
                className="statistics-modal"
                onMouseDown={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                <div className="statistics-modal-header">
                    <div className="statistics-modal-title-wrap">
                        <h2 className="statistics-modal-title">
                            <a
                                href={`/clients/${client?.client_id}`}
                                className="statistics-modal-client-link"
                                title="Open client"
                            >
                                {safeText(client?.display_name)}
                            </a>
                        </h2>
                        <p className="statistics-modal-subtitle">
                            Unit rankings for the last <strong>{days}</strong> days • Client score:{' '}
                            <strong>{formatScore(client?.score)}</strong>
                        </p>
                    </div>

                    <button className="modal-x" onClick={onClose} type="button">
                        ✕
                    </button>
                </div>

                <div className="statistics-modal-body">
                    {loading && (
                        <div className="app-loading">
                            <div className="app-spinner" />
                            <div className="app-spinner-text">Loading Statistics...</div>
                        </div>
                    )}

                    {error && <div className="statistics-modal-error">{error}</div>}

                    {!loading && !error && units.length === 0 && (
                        <div className="statistics-modal-empty">
                            No units found (or no bookings in this period).
                        </div>
                    )}

                    {!loading && !error && units.length > 0 && (
                        <div className="statistics-modal-table">
                            <div className="statistics-modal-table-head">
                                <div className="statistics-modal-th statistics-modal-th-rank">#</div>
                                <div className="statistics-modal-th statistics-modal-th-prop">Property</div>
                                <div className="statistics-modal-th statistics-modal-th-score">Avg / Day</div>
                                <div className="statistics-modal-th statistics-modal-th-bookings">Bookings</div>
                            </div>

                            {units.map((u, idx) => (
                                <div key={`${u.property_id}-${idx}`} className="statistics-modal-tr">
                                    <div className="statistics-modal-td statistics-modal-td-rank">{idx + 1}</div>

                                    <div className="statistics-modal-td statistics-modal-td-prop">
                                        <div className="statistics-modal-prop-top">
                                            <a
                                                href={`/properties/${u.property_id}`}
                                                className="statistics-modal-prop-link"
                                                title="Open property"
                                            >
                                                {formatPropertyId(u.property_id)}
                                            </a>

                                            {u.google_maps_link ? (
                                                <a
                                                    href={u.google_maps_link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="statistics-modal-maps"
                                                >
                                                    Maps
                                                </a>
                                            ) : null}
                                        </div>
                                        <div className="statistics-modal-prop-line">{buildUnitLine(u)}</div>
                                    </div>

                                    <div className="statistics-modal-td statistics-modal-td-score">
                                        {formatScore(u.score)}
                                    </div>

                                    <div className="statistics-modal-td statistics-modal-td-bookings">
                                        {Number(u.bookings_count || 0)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="statistics-modal-footer">
                    <button className="statistics-modal-primary" type="button" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function ClientRankingStat() {
    const [days, setDays] = useState(30);
    const [groups, setGroups] = useState(4);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [groupSummaries, setGroupSummaries] = useState([]);
    const [adminSummaries, setAdminSummaries] = useState([]);
    const [clients, setClients] = useState([]);

    const [search, setSearch] = useState('');

    const [selectedClient, setSelectedClient] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const fetchRankings = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axios.get('/api/statistics/client-rankings', {
                params: { days, groups },
                withCredentials: true,
            });

            setGroupSummaries(Array.isArray(res.data?.group_summaries) ? res.data.group_summaries : []);
            setAdminSummaries(Array.isArray(res.data?.admin_summaries) ? res.data.admin_summaries : []);
            setClients(Array.isArray(res.data?.clients) ? res.data.clients : []);
        } catch (e) {
            console.error('Failed to load statistics', e);
            setError('Failed to load statistics.');
            setGroupSummaries([]);
            setAdminSummaries([]);
            setClients([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRankings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredClients = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return clients;

        return clients.filter((c) => {
            const name = (c.display_name || '').toLowerCase();
            const comp = (c.company_name || '').toLowerCase();
            const id = String(c.client_id || '');
            return name.includes(q) || comp.includes(q) || id.includes(q);
        });
    }, [clients, search]);

    const groupedClients = useMemo(() => {
        const map = new Map();
        for (const c of filteredClients) {
            const gi = Number(c.group_index || 1);
            if (!map.has(gi)) map.set(gi, []);
            map.get(gi).push(c);
        }

        return Array.from(map.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([group_index, list]) => ({
                group_index,
                clients: list.sort((a, b) => Number(b.score) - Number(a.score)),
            }));
    }, [filteredClients]);

    const totalScore = useMemo(() => {
        return clients.reduce((sum, c) => sum + Number(c.score || 0), 0);
    }, [clients]);

    const openClientModal = (client) => {
        setSelectedClient(client);
        setModalOpen(true);
    };

    const closeClientModal = () => {
        setModalOpen(false);
        setSelectedClient(null);
    };

    return (
        <div className="statistics-section">
            <div className="statistics-header">
                <div>
                    <h2 className="statistics-section-title">Client Ranking</h2>
                    <p className="statistics-subtitle">
                        Ranking based on average bookings per day in the last <strong>{days}</strong> days.
                    </p>
                </div>

                <div className="statistics-header-actions">
                    <button
                        className="statistics-refresh-button"
                        type="button"
                        onClick={fetchRankings}
                        disabled={loading}
                    >
                        {loading ? 'Loading...' : 'Refresh'}
                    </button>
                </div>
            </div>

            <div className="statistics-controls">
                <div className="statistics-control-card">
                    <label className="statistics-label">Days window</label>
                    <select
                        className="statistics-select"
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                    >
                        <option value={7}>7 days</option>
                        <option value={14}>14 days</option>
                        <option value={30}>30 days</option>
                        <option value={60}>60 days</option>
                        <option value={90}>90 days</option>
                    </select>
                </div>

                <div className="statistics-control-card">
                    <label className="statistics-label">Groups</label>
                    <select
                        className="statistics-select"
                        value={groups}
                        onChange={(e) => setGroups(Number(e.target.value))}
                    >
                        <option value={2}>2 groups</option>
                        <option value={3}>3 groups</option>
                        <option value={4}>4 groups</option>
                        <option value={5}>5 groups</option>
                        <option value={6}>6 groups</option>
                    </select>
                </div>

                <div className="statistics-control-card statistics-control-card-grow">
                    <label className="statistics-label">Search clients</label>
                    <input
                        className="statistics-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name, company or client id..."
                    />
                </div>

                <button
                    className="statistics-primary-button"
                    type="button"
                    onClick={fetchRankings}
                    disabled={loading}
                >
                    Apply
                </button>
            </div>

            <div className="statistics-summary-row">
                <div className="statistics-summary-card">
                    <div className="statistics-summary-label">Total Clients</div>
                    <div className="statistics-summary-value">{clients.length}</div>
                </div>

                <div className="statistics-summary-card">
                    <div className="statistics-summary-label">Total Avg / Day</div>
                    <div className="statistics-summary-value">{formatScore(totalScore)}</div>
                </div>

                <div className="statistics-summary-card statistics-summary-card-wide">
                    <div className="statistics-summary-label">Group totals</div>
                    <div className="statistics-summary-chips">
                        {groupSummaries.map((g) => (
                            <span key={g.group_index} className="statistics-chip">
                                {g.group_label || `Group ${g.group_index}`}: {formatScore(g.total_score)} ({g.client_count})
                            </span>
                        ))}
                        {groupSummaries.length === 0 && (
                            <span className="statistics-summary-muted">No data yet</span>
                        )}
                    </div>
                </div>
            </div>

            {adminSummaries.length > 0 && (
                <div className="statistics-admin-row">
                    {adminSummaries.map((a) => (
                        <div key={a.admin_id} className="statistics-admin-card">
                            <div className="statistics-admin-top">
                                <div className="statistics-admin-left">
                                    <div className="statistics-admin-name">{safeText(a.admin_name)}</div>

                                    <div className="statistics-admin-combined-row">
                                        <span className="statistics-admin-chip statistics-admin-chip-strong">
                                            Combined Score: <strong>{formatScore(a.combined?.score)}</strong>
                                        </span>
                                        <span className="statistics-admin-chip">
                                            Clients: <strong>{Number(a.combined?.client_count || 0)}</strong>
                                        </span>
                                        <span className="statistics-admin-chip">
                                            Units: <strong>{Number(a.combined?.properties_count || 0)}</strong>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="statistics-admin-grid">
                                <div className="statistics-admin-metric">
                                    <div className="statistics-admin-metric-title">Manager</div>
                                    <div className="statistics-admin-metric-line">
                                        Score: <strong>{formatScore(a.as_manager?.score)}</strong>
                                    </div>
                                    <div className="statistics-admin-metric-line">
                                        Clients: <strong>{Number(a.as_manager?.client_count || 0)}</strong>
                                    </div>
                                    <div className="statistics-admin-metric-line">
                                        Units: <strong>{Number(a.as_manager?.properties_count || 0)}</strong>
                                    </div>
                                </div>

                                <div className="statistics-admin-metric">
                                    <div className="statistics-admin-metric-title">Coordinator</div>
                                    <div className="statistics-admin-metric-line">
                                        Score: <strong>{formatScore(a.as_coordinator?.score)}</strong>
                                    </div>
                                    <div className="statistics-admin-metric-line">
                                        Clients: <strong>{Number(a.as_coordinator?.client_count || 0)}</strong>
                                    </div>
                                    <div className="statistics-admin-metric-line">
                                        Units: <strong>{Number(a.as_coordinator?.properties_count || 0)}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {error && <div className="statistics-error">{error}</div>}

            <div className="statistics-groups">
                {groupedClients.map((g) => (
                    <div key={g.group_index} className="statistics-group-card">
                        <div className="statistics-group-header">
                            <div className="statistics-group-title">
                                {g.clients?.[0]?.group_label || `Group ${g.group_index}`}
                            </div>
                            <div className="statistics-group-meta">{g.clients.length} clients</div>
                        </div>

                        <div className="statistics-table">
                            <div className="statistics-table-head">
                                <div className="statistics-th statistics-th-client">Client</div>
                                <div className="statistics-th statistics-th-score">Avg / Day</div>
                                <div className="statistics-th statistics-th-bookings">Bookings</div>
                                <div className="statistics-th statistics-th-props">Units</div>
                            </div>

                            {g.clients.map((c) => (
                                <button
                                    key={c.client_id}
                                    type="button"
                                    className="statistics-tr"
                                    onClick={() => openClientModal(c)}
                                >
                                    <div className="statistics-td statistics-td-client">
                                        <div className="statistics-client-name">{safeText(c.display_name)}</div>
                                        <div className="statistics-client-sub">
                                            ID {c.client_id} • {safeText(c.company_name)}
                                        </div>
                                    </div>

                                    <div className="statistics-td statistics-td-score">{formatScore(c.score)}</div>
                                    <div className="statistics-td statistics-td-bookings">{Number(c.bookings_count || 0)}</div>
                                    <div className="statistics-td statistics-td-props">{Number(c.properties_count || 0)}</div>
                                </button>
                            ))}

                            {g.clients.length === 0 && (
                                <div className="statistics-empty-row">No clients in this group (after filtering).</div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <StatisticsModal isOpen={modalOpen} onClose={closeClientModal} client={selectedClient} days={days} />
        </div>
    );
}